/**
 * Sagas for logging events to an analytics service. Currently, Google
 * Analytics
 */

import { getNow } from '@sparx/sw-common';
import log from 'loglevel';
import { getCurrentActivityIndex } from 'phoenix2/state/activity/selectors';
import { getConfigParameter } from 'phoenix2/state/config-params/selectors';
import { getShowingQuestion } from 'phoenix2/state/question-activity/selectors';
import { LOGOUT } from 'phoenix2/state/user/actions';
import { asyncSleep } from 'phoenix2/utils';
import { call, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { makeNotification } from 'phoenix2/state/remote-no-ws/sagas';
import { notify } from 'phoenix2/state/remote/sagas';

import {
    analyticsHit,
    ANALYTICS_HIT,
    SET_PAGE_VISIBILITY,
    TIMING_METRIC,
    UPDATE_ANALYTICS_TRACKER,
} from './actions';
import { TimingMetrics } from './timing-metrics';

const METRICS_LOG_INTERVAL_SECS = 60;

/**
 * Watches analytics events and makes the appropriate calls to the analytics
 * engine (currently, Google Analytics)
 */
export function* watchAnalyticsEvents(analytics) {
    yield takeEvery([UPDATE_ANALYTICS_TRACKER, ANALYTICS_HIT], doAnalytics, analytics);
}

/**
 * For a given analytic event, make the appropriate call to the analytics engine
 * @param action
 */
export function* doAnalytics(analytics, action) {
    switch (action.type) {
        case UPDATE_ANALYTICS_TRACKER:
            analytics.setProperties(action.fieldsObject);
            break;
        case ANALYTICS_HIT:
            const timestamp = getNow();

            if (action.hitType === 'pageview') {
                analytics.pageview(action.fieldsObject.page, timestamp);
            } else {
                // Add the current activityIndex to the event labels if there is
                // one:
                let eventLabel = action.fieldsObject.eventLabel;
                const activityIndex = yield select(getCurrentActivityIndex);
                if (activityIndex !== null && activityIndex !== undefined) {
                    if (!eventLabel) {
                        eventLabel = {};
                    }
                    eventLabel.activityIndex = String(activityIndex);
                }

                analytics.event(
                    action.fieldsObject.eventAction,
                    action.fieldsObject.eventCategory,
                    eventLabel,
                    timestamp,
                );
            }
            break;
    }
}

let metricsStore = null;

/**
 * Start gathering timing metrics, sending them to Google Analytics. Also,
 * periodically send them to the server to be logged
 */
export function* gatherMetrics(session, analytics) {
    // Create a metric store if one does not exist. Note, reuse previous
    // store if sagas were restarted so any metrics left-over from before
    // are sent:
    if (!metricsStore && analytics.useWs) {
        metricsStore = new TimingMetrics();
    }

    try {
        // Listen for new timing metrics to add to the store and send to Google
        // Analytics:
        yield fork(watchTimingMetrics, metricsStore, analytics);

        // Periodically send metrics to the server:
        if (metricsStore) {
            yield fork(periodicallyLogMetrics, metricsStore, METRICS_LOG_INTERVAL_SECS);
        }

        // Send the analytics if the user logs out so they are not lost:
        yield take(LOGOUT);
        //yield call(doLogMetrics, metricsStore);
    } finally {
        // If the saga is ended (either logout, or cancelled saga), send any
        // remaining stats to the server:
        analytics.flush();
        if (metricsStore) {
            const metrics = metricsStore.getAll();
            try {
                yield call(makeNotification, session, {
                    method: 'reportTimingMetrics',
                    args: [metrics],
                });
            } catch (exc2) {
                log.warn(
                    'Analytics could not be reported to the server after' +
                        ' gatherMetrics saga ended',
                );
            }
        }
    }
}

export function* watchTimingMetrics(metricsStore, analytics) {
    yield takeEvery(TIMING_METRIC, recordTimingMetric, metricsStore, analytics);
}

/**
 * Record a new timing metric, ready for sending to the server, and, if
 * required, to Google Analytics
 * @param metricsStore metrics store
 * @param analytics Analytics object
 * @param action TIMING_METRIC action containing the details
 */
export function recordTimingMetric(metricsStore, analytics, action) {
    analytics.timing(action.variable, action.category, action.timeMillis);

    // Record for sending to server via websocket
    if (metricsStore) {
        metricsStore.add(action.category, action.variable, action.timeMillis);
    }
}

/**
 * Send metrics to the server periodically
 * @param metricsStore
 * @param defaultIntervalSecs Default interval to wait, if there is no config
 * parameter specifying an interval
 */
export function* periodicallyLogMetrics(metricsStore, defaultIntervalSecs) {
    // Wait 1s to give time for interval config parameter to be received:
    yield asyncSleep(1);

    while (true) {
        // Check config parameters for how long an interval to wait between
        // sending logs:
        const configInterval = yield select(getConfigParameter, 'analyticsLogInterval');

        let interval = defaultIntervalSecs;

        if (configInterval) {
            interval = parseInt(configInterval);
        }

        // Wait for the interval:
        yield asyncSleep(interval);

        yield call(doLogMetrics, metricsStore);
    }
}

/**
 * Send analytics metrics to the server
 * @param metricsStore
 */
export function* doLogMetrics(metricsStore) {
    if (metricsStore.getCount() > 0) {
        const metrics = metricsStore.getAll();

        // Clear store to start collecting the next batch of metrics:
        metricsStore.clear();

        // Send the metrics to the server:
        yield call(notify, 'reportTimingMetrics', metrics);
    }
}

/**
 * Watches analytics events and makes the appropriate calls to the analytics
 * engine (currently, Google Analytics)
 */
export function* watchPageVisibility() {
    yield takeEvery(SET_PAGE_VISIBILITY, doPageVisibility);
}

/**
 * When a page visibility action is received, send an analytics event, including
 * details of the current question, if the user is doing one
 * @param hidden whether the page is hidden
 * @returns {IterableIterator<*>}
 */
function* doPageVisibility({ hidden }) {
    // We want to include details of the question activity the user is doing,
    // if applicable:
    const showingQuestion = yield select(getShowingQuestion);

    const label = {
        skillID: '',
        activityIndex: '',
    };

    if (showingQuestion) {
        (label.skillID = showingQuestion.get('skillID')),
            (label.activityIndex = showingQuestion.get('activityIndex'));
    }

    yield put(
        analyticsHit('event', {
            eventCategory: 'page visibility change',
            eventAction: hidden ? 'not visible' : 'visible',
            eventLabel: label,
        }),
    );
}
